import _ from 'lodash'
import React, { Suspense, useEffect, useState } from 'react'
import { useTranslation, withTranslation, WithTranslation } from 'react-i18next'
import { connect, useDispatch, useSelector } from 'react-redux'
import { Dispatch } from 'redux'
import styled, { css } from 'styled-components'
import { HeaderWidgetManager, Types } from '@briq/shared'
import { useLocation, useNavigate } from 'react-router'
import { theme } from '@briq/shared'

import { useSessionStorage } from '../../hooks/useSessionStorage'
import { useViewportSize } from '../../hooks/useViewportSize'
import {
  getCmsHomeUrl,
  getExternalSearchLink,
  getFooterLinks,
  getFooterText,
  getNavigationLinks,
  getSearchFilters,
  getSearchWidgetDisplayMode,
  getCountryFilterEnabled,
  getDefaultCountryFilter,
  getOtherCountriesFilter,
  isApiBlocked,
  Link as ConfigLink,
  NavigationLink,
  getHeaderBanner,
  getCmsFaqUrl,
  getLogoUrl,
  getShowBulkImportMenuInHeader,
  getShowBulkImportLinkAboveSearchButton,
  getShowAsBulkImportButton,
  getMobileLogoInvertedUrl,
  getLogoInvertedUrl,
  getContactWidget,
  getShowMulticolumnDropdown,
  getCountryCode,
  getContactUsFormWidget,
  getContactUsReason,
  getShowAiChatbotButton,
  getSapAnonymousUserId,
  getSessionTimeout
} from '../../redux/config'
import { OrderActions, getNumberOfOrderItems } from '../../redux/order'
import { RootState } from '../../redux/types'
import { formatPath, parseGetParams, stringifyGetParams } from '../../utils/routes'
import { Box } from '../Box'
import { Flex } from '../Flex'
import { Helmet } from '../Helmet'
import { breakpoint } from '../theme'
import { NavMenuActions, getSelectedMenuItem } from '../../redux/navMenu'
import { AccountNotificationsActions, getUnreadNotificationsCount } from '../../redux/account/notifications'
import {
  getFullName,
  getPaymentOptionCode,
  getSubscriberExternalId,
  getUserRoles,
  getUsername,
  isUserLoggedIn
} from '../../redux/user/selectors'
import {
  useApi,
  useAuthorizations,
  useChangeLanguage,
  useCurrentRoute,
  useLinkGenerator,
  useLoginUrl
} from '../../hooks'
import { UserActions } from '../../redux/user'
import { Routes } from '../../routes'
import { Link } from '../Link'

import { Container } from './Container'
import { Footer } from './Footer'
import { NavMenu } from './NavMenu'

const LayoutContainer = styled(Flex)<{ hideScroll?: boolean }>`
  min-height: 100%;
  max-width: 100%;
  overflow-x: clip;
  flex-direction: column;

  ${({ hideScroll }) =>
    hideScroll &&
    `
      max-height: 100%;
      overflow: hidden;
  `}
`

const PageContainer = styled.div<{ backgroundColor?: theme.Color }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  flex: 1 1 auto;
  overflow: clip;
  ${({ theme, backgroundColor }) =>
    backgroundColor &&
    `
    background-color: ${theme.colors[backgroundColor]};
  `}
`

// MVPHIDDEN
// const NeedHelpContainer = styled.div`
//   position: relative;
//   height: 60px;
// `

// const NeedHelpFixation = styled.div`
//   position: absolute;
//   right: 0;
//   bottom: 0;
//   z-index: 20;
// `

const StyledContainer = styled(Container)`
  position: relative;
  flex: 1 1 auto;
`

const StyledFooterContainer = styled(Box)`
  margin-top: 80px;
  z-index: 10;

  ${breakpoint.tablet(css`
    nav {
      display: none;
    }
  `)}
`

const FlexLink = styled(Link)`
  display: flex;
`

const BulkImportLink = styled(Link)`
  font-weight: bold;
  display: flex;
  align-items: center;
`

const mapStateToProps = (state: RootState) => ({
  searchFilterParams: getSearchFilters(state)
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  loadOrderMetadata: () => dispatch(OrderActions.loadOrderMetadata())
})

const mapLink = (l: ConfigLink) => {
  l.url = l.url ? formatPath(l.url) : ''
  l.text = l.text || ''
  return l
}

type OuterProps = { backgroundColor?: theme.Color; render: (apiBlocked: boolean) => React.ReactNode }

export type PageLayoutProps = WithTranslation &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  OuterProps

const createNavLinks = (navigationLinks: NavigationLink[]) => {
  return _.map(navigationLinks, l => {
    if (l.linkModel) {
      l.linkModel = mapLink(l.linkModel)
    }
    if (l.dropdownLinksMenuModel) {
      l.dropdownLinksMenuModel.links = _.map(l.dropdownLinksMenuModel.links, l => mapLink(l))
    }
    return l
  })
}

export const Header = () => {
  const archiveRoutes = [Routes.ArchiveReports, Routes.ArchiveNotifications]
  const profileRoutes = [Routes.Accounting, Routes.Profile, Routes.ProfileManagement, Routes.Transactions]
  const importRoutes = [Routes.UploadImport, Routes.ImportHistory, Routes.Import]

  const location = useLocation()

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchWidgetDisplayMode = useSelector(getSearchWidgetDisplayMode)
  const searchFilters = useSelector(getSearchFilters)
  const countryFilterEnabled = useSelector(getCountryFilterEnabled)
  const defaultCountryFilter = useSelector(getDefaultCountryFilter)
  const otherCountriesFilter = useSelector(getOtherCountriesFilter)
  const navigationLinks = useSelector(getNavigationLinks)
  const externalSearchLink = useSelector(getExternalSearchLink)
  const cmsHomeUrl = useSelector(getCmsHomeUrl)
  const cmsFaqUrl = useSelector(getCmsFaqUrl)
  const banner = useSelector(getHeaderBanner)
  const loggedIn = useSelector(isUserLoggedIn)
  const userName = useSelector(getUsername)
  const fullName = useSelector(getFullName)
  const paymentOptionCode = useSelector(getPaymentOptionCode)
  const subscriberExternalId = useSelector(getSubscriberExternalId)
  const logoUrl = useSelector(getLogoUrl)
  const countOfOrderItems = useSelector(getNumberOfOrderItems)
  const roles = useSelector(getUserRoles)
  const loginUrl = useLoginUrl()
  const showBulkImportMenuInHeader = useSelector(getShowBulkImportMenuInHeader)
  const showBulkImportLinkAboveSearchButton = useSelector(getShowBulkImportLinkAboveSearchButton)
  const showAsBulkImportButton = useSelector(getShowAsBulkImportButton)
  const countUnreadNotifications = useSelector(getUnreadNotificationsCount)
  const selectedMenuItem = useSelector(getSelectedMenuItem)
  const footerLinks = useSelector(getFooterLinks)
  const logoImg = useSelector(getMobileLogoInvertedUrl)
  const showMulticolumnDropdown = useSelector(getShowMulticolumnDropdown)
  const contactWidget = useSelector(getContactWidget)
  const showContactUsFormWidget = useSelector(getContactUsFormWidget)
  const contactUsReason = useSelector(getContactUsReason)
  const showAiChatbotButton = useSelector(getShowAiChatbotButton)
  const sapAnonymousUserId = useSelector(getSapAnonymousUserId)
  const sessionTimeout = useSelector(getSessionTimeout)
  const { isAuthorizedForOrdering } = useAuthorizations()
  const [contactRequestDone, setContactRequestDone] = useState<boolean>(false)
  const [contactRequestError, setContactRequestError] = useState<any>(undefined)

  const { route } = useCurrentRoute()
  const { generateRoute } = useLinkGenerator()

  const { changeLanguage } = useChangeLanguage()

  const { request, loading, done, error } = useApi()
  const domainCode = useSelector(getCountryCode)
  const { i18n } = useTranslation()

  const viewPortSize = useViewportSize()
  const isDesktop = viewPortSize && (viewPortSize.isDesktop || viewPortSize.isWide)

  const [closedBannerId, setClosedBannerId] = useSessionStorage('closedBanner', '')

  useEffect(() => {
    if (isDesktop && loggedIn) {
      dispatch(AccountNotificationsActions.fetchNotificationsStats())
    }
  }, [dispatch, isDesktop, loggedIn])

  useEffect(() => {
    setContactRequestDone(done)
    setContactRequestError(error)
  }, [done, error])

  const queryParams = parseGetParams(location.search)

  useSessionStorage(
    'isLoginAs',
    _.isString(queryParams.isLoginAs) &&
      _.size(_.toString(queryParams.isLoginAs)) > 0 &&
      route?.key === Routes.ArchiveReports
      ? queryParams.isLoginAs
      : ''
  )

  return (
    <HeaderWidgetManager.HeaderWidgetManager
      ImportHistoryLink={({ children }) => <Link to={Routes.ImportHistory}>{children}</Link>}
      activeButton={
        _.includes(archiveRoutes, route?.key)
          ? 'Archive'
          : _.includes(profileRoutes, route?.key)
            ? 'Profile'
            : _.includes(importRoutes, route?.key)
              ? 'BulkImport'
              : undefined
      }
      bannerData={banner}
      changeLanguage={changeLanguage}
      close={name => dispatch(NavMenuActions.close({ name }))}
      closedBannerId={closedBannerId}
      cmsFaqUrl={cmsFaqUrl}
      cmsHomeUrl={cmsHomeUrl}
      countOfOrderItems={countOfOrderItems}
      countUnreadNotifications={countUnreadNotifications}
      countryFilterEnabled={countryFilterEnabled}
      footerLinks={footerLinks}
      fullName={fullName}
      handleMarkAllNotificationsReadClick={() => dispatch(AccountNotificationsActions.markAllNotificationsAsRead())}
      hasBriqBulkImportRole={_.includes(roles, Types.UserRole.BRIQBulkImport)}
      links={{
        Archive: ({ children }) => <FlexLink to={Routes.ArchiveReports}>{children}</FlexLink>,
        Profile: ({ children }) => <FlexLink to={Routes.Profile}>{children}</FlexLink>,
        Bulk: ({ children }) => <FlexLink to={Routes.ImportHistory}>{children}</FlexLink>,
        Cart: ({ children }) => <Link to={Routes.Cart}>{children}</Link>,
        Notifications: ({ children }) => <Link to={Routes.ArchiveNotifications}>{children}</Link>,
        AdditionalBulkHistory: ({ children }) => <BulkImportLink to={Routes.ImportHistory}>{children}</BulkImportLink>,
        Home: ({ children }) => <Link to="home">{children}</Link>,
        Slash: ({ children }) => <Link to="/">{children}</Link>
      }}
      loggedIn={loggedIn}
      loginAsLogout={() => dispatch(UserActions.logout({ isLoginAsMode: true }))}
      loginUrl={loginUrl}
      logoImg={logoImg}
      logoUrl={logoUrl}
      logout={() => dispatch(UserActions.logout({ isLoginAsMode: false }))}
      navigationLinks={navigationLinks}
      onSearchClick={values =>
        navigate({
          pathname: generateRoute(Routes.Search),
          search: stringifyGetParams(values)
        })
      }
      queryString={location.search}
      searchFilters={searchFilters}
      selectedMenuItem={selectedMenuItem}
      setClosedBannerId={setClosedBannerId}
      showAsBulkImportButton={showAsBulkImportButton}
      showBulkImportLinkAboveSearchButton={showBulkImportLinkAboveSearchButton}
      showBulkImportMenuInHeader={showBulkImportMenuInHeader}
      toggle={name => dispatch(NavMenuActions.toggle({ name }))}
      userName={userName}
      defaultCountryFilter={defaultCountryFilter}
      externalSearchLink={externalSearchLink}
      otherCountriesFilter={otherCountriesFilter}
      searchWidgetDisplayMode={searchWidgetDisplayMode}
      showMulticolumnDropdown={showMulticolumnDropdown}
      contactWidget={contactWidget}
      handleContactUsRequestSubmit={async (values: HeaderWidgetManager.ContactUsDetails) => {
        await request({
          method: 'post',
          url: '/api/notification/contact-us',
          data: {
            name: values.name,
            contact: values.phone,
            reason: values.reason,
            message: values.message,
            domainCode: domainCode,
            language: i18n.language,
            url: values.url,
            email: values.email
          }
        })
      }}
      contactRequestLoading={loading}
      contactRequestError={contactRequestError}
      contactRequestDone={contactRequestDone}
      showContactUsFormWidget={showContactUsFormWidget}
      contactUsReason={contactUsReason?.translations}
      resetContactUs={() => {
        setContactRequestDone(false)
        setContactRequestError(undefined)
      }}
      showAiChatbotButton={showAiChatbotButton}
      paymentOptionCode={paymentOptionCode}
      userId={userName}
      subscriberExternalId={subscriberExternalId}
      domainCode={domainCode}
      anonymousSubscriberId={sapAnonymousUserId}
      sessionTimeout={sessionTimeout}
      isAuthorizedForOrdering={isAuthorizedForOrdering}
    />
  )
}

export const StandAloneFooter = () => {
  const footerText = useSelector(getFooterText)
  const footerLinks = useSelector(getFooterLinks)
  const logoUrl = useSelector(getLogoInvertedUrl)
  const logoMobileUrl = useSelector(getMobileLogoInvertedUrl)
  const contactWidget = useSelector(getContactWidget)

  return (
    <Footer
      footerCmsHtmlContent={footerText}
      links={_.map(footerLinks, fl => ({ ...fl, url: formatPath(fl.url) }))}
      logoUrl={logoUrl}
      logoMobileUrl={logoMobileUrl}
      contactWidget={contactWidget}
    />
  )
}

export const StandAloneHeader = () => {
  return (
    <Suspense fallback={false}>
      <Header></Header>
    </Suspense>
  )
}

export const PageLayoutComponent = ({ backgroundColor, loadOrderMetadata, render }: PageLayoutProps) => {
  const dispatch = useDispatch()

  const footerText = useSelector(getFooterText)
  const footerLinks = useSelector(getFooterLinks)
  const navigationLinks = useSelector(getNavigationLinks)
  const apiBlocked = useSelector(isApiBlocked)
  const selectedMenuItem = useSelector(getSelectedMenuItem)
  const logoUrl = useSelector(getLogoInvertedUrl)
  const logoMobileUrl = useSelector(getMobileLogoInvertedUrl)
  const contactWidget = useSelector(getContactWidget)

  const navMenuItems = createNavLinks(navigationLinks)

  useEffect(() => {
    loadOrderMetadata()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <LayoutContainer className="qa--layout-container">
      <Helmet>
        <title>{window.location.href}</title>
      </Helmet>
      <PageContainer backgroundColor={backgroundColor}>
        <Suspense fallback={false}>
          <Header />
        </Suspense>
        <StyledContainer>{render(apiBlocked)}</StyledContainer>
        <StyledFooterContainer>
          {/* MVPHIDDEN
          <NeedHelpContainer>
            <NeedHelpFixation>
              <NeedHelp
                text="If you need any help our customer support is available MON - FRI from 09:00 to 18:00"
                phone="+00 223125125"
                icon={<PhoneOutlined />}
              />
            </NeedHelpFixation>
          </NeedHelpContainer>
           */}
          <NavMenu
            variant="primary"
            items={navMenuItems}
            toggle={name => dispatch(NavMenuActions.toggle({ name }))}
            close={name => dispatch(NavMenuActions.close({ name }))}
            isVisible={name => name === selectedMenuItem}
          />
          <Footer
            footerCmsHtmlContent={footerText}
            links={_.map(footerLinks, fl => ({ ...fl, url: formatPath(fl.url) }))}
            logoUrl={logoUrl}
            contactWidget={contactWidget}
            logoMobileUrl={logoMobileUrl}
          />
        </StyledFooterContainer>
      </PageContainer>
    </LayoutContainer>
  )
}

type PageLayoutComponent = React.ComponentClass<OuterProps>

export const PageLayout: PageLayoutComponent = connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(PageLayoutComponent)) as any
